/**
 * The mermaid configuration, as one object PER THEME that both the app and the verifier read.
 *
 * A diagram in a thread sits inches from the workflow editor's own graph, so the two should not
 * look like they came from different products. These values are `flowkit`'s — the node fill,
 * border and title from `flowkit.css`, the connector stroke from `flowEdge.js` — mapped onto
 * mermaid's `base` theme, which is the only built-in meant to be customised: it derives its
 * remaining ~100 variables from these roots, where `default`/`neutral`/`forest`/`dark` fight them.
 *
 * WHY THIS BRIDGE IS JAVASCRIPT AND NOT CSS. mermaid resolves themeVariables at RENDER time and
 * bakes the results into the SVG string it returns — as inline fill/stroke attributes on the shapes
 * and as a generated <style> block scoped to the diagram's own id. Nothing in that output reads a
 * CSS custom property, and handing mermaid a `var()` would feed it to its own colour maths (it
 * lightens, darkens and computes contrast against these roots internally) and produce garbage. It
 * needs concrete colours, and a theme change needs a RE-RENDER rather than a repaint — see
 * markdown.js.
 *
 * `secure` is set EXPLICITLY. Mermaid lets a diagram carry its own config in a `---` front-matter
 * block or a `%%{init}%%` directive, and `securityLevel: "strict"` does not stop it — strict governs
 * HTML in labels, while `secure` is the list of keys a directive may not touch. Its default does not
 * include `theme`, so without this an agent-authored artifact could render itself in `forest` inside
 * our pane. These definitions arrive over the network like any other artifact; appearance is ours.
 *
 * Kept in sync with `tools/mermaid/config.json` (light) and `tools/mermaid/config.dark.json` (dark)
 * by `mermaidTheme.test.js` — the verifier renders real diagrams through mermaid-cli, and a palette
 * it did not share would verify nothing. BOTH are rendered by `tools/mermaid/render.sh`; a dark set
 * nobody ever looks at is exactly how a theme rots.
 */

// Everything that is not a colour. Shared by both palettes, which is what stops the two drifting
// apart on geometry — the half of the config nobody thinks to diff.
const MERMAID_BASE = {
  startOnLoad: false,
  securityLevel: 'strict',
  secure: ['secure', 'securityLevel', 'startOnLoad', 'maxTextSize', 'suppressErrorRendering',
           'theme', 'themeVariables', 'themeCSS', 'fontFamily'],
  theme: 'base',
  fontFamily: 'inherit',
  // ROOT `fontSize`, not `themeVariables.fontSize`, and not `sequence.actorFontSize`.
  //
  // A sequence diagram rendered a full size larger than everything beside it — 19 elements at 16px
  // against the flowchart's 13 — and the obvious knobs do nothing, silently. Its renderer assigns
  // the per-element sizes and then OVERWRITES all three from one value:
  //
  //     e.fontSize && (actorFontSize = noteFontSize = messageFontSize = e.fontSize)
  //
  // so `sequence.actorFontSize: 13` is applied and then thrown away. `themeVariables.fontSize`
  // never reaches it at all. Only this key works; measured every time, since none of it is
  // discoverable from the config types.
  fontSize: 13,
  // Geometry, tuned against the state diagram — the one that already read as compact. Mermaid's
  // defaults are sized for a full page; in a thread pane beside prose they left nodes floating in
  // padding with a rank of whitespace between them.
  flowchart: {
    // Also the only lever on a decision diamond, whose side mermaid computes as
    // `(labelWidth + padding) + (labelHeight + padding)` — a sum, so a wide label makes a TALL
    // diamond and no config reshapes it. Measured: 122 → 114 across this drop, against 126x36 →
    // 110x28 for a rectangle. Wrapping the label makes it worse, not better (135), because
    // wrapping trades width for height and the formula adds them.
    padding: 4,           // inside a node, was 15
    nodeSpacing: 30,      // between siblings, was 50
    rankSpacing: 36,      // between ranks — the gap that read as a gutter, was 50
    diagramPadding: 8
  },
  sequence: {
    width: 120,           // an actor box, was 150
    height: 44,           // was 65
    actorMargin: 30,      // between lifelines, was 50
    boxMargin: 8,         // was 10
    messageMargin: 26,    // between messages, was 35
    diagramMarginX: 16,   // was 50
    diagramMarginY: 10    // was 10
  }
};

// LIGHT — unchanged, value for value, from what shipped. Nothing in this object moves.
export const MERMAID_LIGHT_VARIABLES = {
  darkMode: false,
  background: '#ffffff',
  fontFamily: 'inherit',
  fontSize: '13px',

  // A node: flowkit's flat square — light slate fill, a quiet border, near-black title.
  primaryColor: '#f2f4f6',
  primaryBorderColor: '#c8d1da',
  primaryTextColor: '#1f2937',
  mainBkg: '#f2f4f6',
  nodeBorder: '#c8d1da',
  textColor: '#1f2937',

  // The emphasised node — flowkit's selected state.
  secondaryColor: '#e8ebee',
  secondaryBorderColor: '#adb7c2',
  secondaryTextColor: '#1f2937',

  // The quiet surface — flowkit's tag chip.
  tertiaryColor: '#ffffff',
  tertiaryBorderColor: '#dfe3e8',
  tertiaryTextColor: '#5b6572',

  // Connectors: the outlined slate `flowEdge` draws, and its labels.
  lineColor: '#9aa3af',
  edgeLabelBackground: '#ffffff',
  arrowheadColor: '#9aa3af',

  // Notes and clusters.
  noteBkgColor: '#ffffff',
  noteBorderColor: '#dfe3e8',
  noteTextColor: '#5b6572',
  clusterBkg: '#fafbfc',
  clusterBorder: '#dfe3e8',
  titleColor: '#1f2937',

  // Sequence diagrams derive little from the roots, so their actors are set directly.
  actorBkg: '#f2f4f6',
  actorBorder: '#c8d1da',
  actorTextColor: '#1f2937',
  actorLineColor: '#c8d1da',
  signalColor: '#9aa3af',
  signalTextColor: '#1f2937',
  labelBoxBkgColor: '#f2f4f6',
  labelBoxBorderColor: '#c8d1da',
  labelTextColor: '#1f2937',
  loopTextColor: '#5b6572',
  activationBkgColor: '#e8ebee',
  activationBorderColor: '#adb7c2',
  sequenceNumberColor: '#ffffff',

  // Entity-relationship rows alternate, and both shades stay inside the palette.
  attributeBackgroundColorOdd: '#ffffff',
  attributeBackgroundColorEven: '#f7f8fa'
};

// DARK — the same 40 keys, in the same order, resolved from --bh-*. Every value is a token's dark
// literal, because mermaid cannot read a variable (see the header); kept literal-for-literal beside
// the light column above so a reviewer can diff the two by eye, and asserted key-for-key by
// mermaidTheme.test.js so one cannot grow an entry the other lacks.
//
//   darkMode: true IS NOT COSMETIC. `base` reads it to choose the DIRECTION of every internal
//   lighten()/darken() it applies to the ~100 variables it derives from these roots. Left false on a
//   dark palette, mermaid derives its secondary shades by lightening values that are already dark
//   and darkening values that are already light, and every derived surface lands on the wrong side
//   of its root — while the roots themselves still look right, so the bug reads as "mermaid is
//   broken".
//
//   `background` is --bh-surface (#202024), not --bh-canvas: a diagram renders inside a thread pane,
//   which is a card. Its light counterpart is #ffffff for exactly the same reason.
//
//   EVERY VALUE BELOW IS A --bh-* TOKEN'S DARK LITERAL, so the palette moves when the token layer
//   does even though mermaid cannot read a variable. The ladder: node fill --bh-fill (#26262b) on
//   the card, node border --bh-border-strong (#45454d), ink --bh-text (#e4e4e7); the emphasised node
//   is --bh-fill-strong over --bh-border-emphasis; the quiet chip is the card itself over
//   --bh-border with --bh-text-muted. clusterBkg is --bh-canvas (#171719), one rung BELOW the card,
//   which is the same direction light takes (#fafbfc under #ffffff) with the larger step a
//   low-luminance ground needs: 1.10:1 against light's 1.04:1.
//
//   lineColor is --bh-edge's dark value (#5c5c66), which is what Phase 5 gave flowkit's own edges,
//   so a diagram and the editor's graph stay one product. It measures 2.46:1 on the diagram ground
//   against light's #9aa3af at 2.55:1 on white — the same connector both ways up. BOTH columns are
//   now --bh-edge exactly: Phase 5 found the token's light value had been declared at #9ca3af while
//   painting nothing, and retuned it to #9aa3af, the literal the edges have always drawn. So there
//   is no pair left to collapse here. sequenceNumberColor is the card again, because that number is
//   punched OUT of a lineColor-filled circle.
export const MERMAID_DARK_VARIABLES = {
  darkMode: true,
  background: '#202024',
  fontFamily: 'inherit',
  fontSize: '13px',

  // A node: flowkit's flat square, one rung up from the card it sits on.
  primaryColor: '#26262b',
  primaryBorderColor: '#45454d',
  primaryTextColor: '#e4e4e7',
  mainBkg: '#26262b',
  nodeBorder: '#45454d',
  textColor: '#e4e4e7',

  // The emphasised node — flowkit's selected state.
  secondaryColor: '#35353c',
  secondaryBorderColor: '#6b6b75',
  secondaryTextColor: '#e4e4e7',

  // The quiet surface — flowkit's tag chip.
  tertiaryColor: '#202024',
  tertiaryBorderColor: '#2e2e33',
  tertiaryTextColor: '#a1a1aa',

  // Connectors: the outlined slate `flowEdge` draws, and its labels.
  lineColor: '#5c5c66',
  edgeLabelBackground: '#202024',
  arrowheadColor: '#5c5c66',

  // Notes and clusters.
  noteBkgColor: '#202024',
  noteBorderColor: '#2e2e33',
  noteTextColor: '#a1a1aa',
  clusterBkg: '#171719',
  clusterBorder: '#2e2e33',
  titleColor: '#e4e4e7',

  // Sequence diagrams derive little from the roots, so their actors are set directly.
  actorBkg: '#26262b',
  actorBorder: '#45454d',
  actorTextColor: '#e4e4e7',
  actorLineColor: '#45454d',
  signalColor: '#5c5c66',
  signalTextColor: '#e4e4e7',
  labelBoxBkgColor: '#26262b',
  labelBoxBorderColor: '#45454d',
  labelTextColor: '#e4e4e7',
  loopTextColor: '#a1a1aa',
  activationBkgColor: '#35353c',
  activationBorderColor: '#6b6b75',
  sequenceNumberColor: '#202024',

  // Entity-relationship rows alternate, and both shades stay inside the palette.
  attributeBackgroundColorOdd: '#202024',
  attributeBackgroundColorEven: '#26262b'
};

/**
 * The light config, kept under its historical name and with its historical shape so
 * `mermaidTheme.test.js` and `tools/mermaid/config.json` compare against it unchanged.
 */
export const MERMAID_CONFIG = { ...MERMAID_BASE, themeVariables: MERMAID_LIGHT_VARIABLES };

export const MERMAID_DARK_CONFIG = { ...MERMAID_BASE, themeVariables: MERMAID_DARK_VARIABLES };

/**
 * `theme` is the RESOLVED theme — "light" or "dark", never "system". Theme.resolved() is what
 * produces it; markdown.js calls this before every render pass, because mermaid.initialize() is a
 * module-global side effect and the palette can only be changed by calling it again.
 */
export function mermaidConfig(theme) {
  return 'dark' === theme ? MERMAID_DARK_CONFIG : MERMAID_CONFIG;
}

export default MERMAID_CONFIG;
